
  import {defineComponent, ref, inject, onMounted, PropType} from "vue";
  import {AxiosError} from "axios";
  import {isAuthorized, t, tWithParms} from "@/mixin/mixins";
  import OrderInfoCard from "@/views/Orders/Info/OrderInfoCard.vue";
  import {ImportedTaskRequest, TaskImport} from "@/model/OrderTaskImport";
  import Order from "@/model/Order";
  import ValidationError from "@/model/ValidationError";
  import OrderTaskService from "@/service/OrderTaskService";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";

  // A task row of the CSV file and its line number in the file
  interface CsvTask {
    line: number;
    task: ImportedTaskRequest;
  }

  // The path of a validation error on one task, e.g. "tasks[3].employeeNumber"
  const TASK_ERROR_PATH = /^tasks\[(\d+)]\.(\w+)$/;

  export default defineComponent({
    name: "OrderInfoTasks",
    components: {
      OrderInfoCard
    },
    props: {
      value: {
        type: Object as PropType<Order>,
        required: false,
        default: () => null
      }
    },
    setup(props) {
      const orderUuid = () => props.value?.uuid;
      // The backend requires both authorities to import or delete tasks
      const canManageImport = isAuthorized("Order") && isAuthorized("Inventory_ViewTasksManager");

      let fileInput = ref<HTMLInputElement | null>(null);
      const currentImport = ref<TaskImport | null>(null);

      let appRoot = inject("app-root");
      // @ts-ignore
      const confirmDialog = appRoot.confirmDialogComposition as InstanceType<typeof ConfirmDialogComposition>;

      onMounted(() => {
        loadImport();
      });

      const loadImport = () => {
        const uuid = orderUuid();
        if (uuid) {
          OrderTaskService.getTaskImport(uuid)
            .then(response => {
              currentImport.value = response.data;
            })
            .catch(() => {
              // 404: the order has no import yet
              currentImport.value = null;
            });
        }
      };

      const onImport = () => {
        fileInput?.value?.click();
      };

      const onDelete = () => {
        const title = t("order.orderInfo.tasks.deleteTasks");
        const text = t("order.orderInfo.tasks.deleteTasksText");
        confirmDialog.open(title, text)
          .then((confirmResult: boolean) => {
            if (confirmResult) {
              deleteImport();
            }
          });
      };

      const readFile = (event: Event) => {
        const fileInput = event?.target as HTMLInputElement;
        const file = fileInput?.files?.[0];
        if (file && file.type === "text/csv") {
          readFileEncoding(file).then((csvContent) => {
            importTasks(toCsvTasks(csvContent));
            fileInput.value = "";
          });
        } else {
          showImportError(t("order.orderInfo.tasks.importTasksErrorText"));
        }
      };

      const readFileEncoding = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event: ProgressEvent<FileReader>) => {
            const buffer = event.target?.result as ArrayBuffer;
            const utf8Decoder = new TextDecoder("utf-8");
            const utf8Text = utf8Decoder.decode(buffer);
            if (containsInvalidUtf8Characters(utf8Text)) {
              console.warn("UTF-8 failed, attempting ISO 8859-1...");
              const isoDecoder = new TextDecoder("iso-8859-1");
              const isoText = isoDecoder.decode(buffer);
              resolve(isoText);
            } else {
              resolve(utf8Text);
            }
          };
          reader.onerror = () => reject(new Error("File reading error"));
          reader.readAsArrayBuffer(file);
        });
      };

      const containsInvalidUtf8Characters = (text: string): boolean => {
        return [...text].some(char => char === "\ufffd");
      };

      // Columns: label; positions; employee name (ignored); employee number; priority. The first row is the header.
      const toCsvTasks = (csvContent: string): CsvTask[] => {
        return csvContent.split("\n")
          .map((row, index) => ({line: index + 1, values: row.split(";").map(value => value.trim())}))
          .filter(row => row.values.some(value => value !== ""))
          .slice(1)
          .map(row => ({
            line: row.line,
            task: {
              label: row.values[0] ?? "",
              positions: parseInt(row.values[1], 10) || 0,
              employeeNumber: toNumberOrNull(row.values[3]),
              priority: toNumberOrNull(row.values[4])
            }
          }));
      };

      const toNumberOrNull = (value: string | undefined): number | null => {
        const number = parseInt(value ?? "", 10);
        return isNaN(number) ? null : number;
      };

      const importTasks = (csvTasks: CsvTask[]) => {
        OrderTaskService.importTasks(orderUuid() as string, {tasks: csvTasks.map(csvTask => csvTask.task)})
          .then(response => {
            currentImport.value = response.data;
          })
          .catch((error: AxiosError<{ errors?: ValidationError[] }>) => {
            const errors = error.response?.status === 422 ? error.response.data?.errors ?? [] : [];
            showImportError(toImportErrorText(errors, csvTasks));
          });
      };

      // Names the CSV line and column of each rejected task, so the file can be corrected
      const toImportErrorText = (errors: ValidationError[], csvTasks: CsvTask[]): string => {
        const lineErrors = errors
          .map(error => TASK_ERROR_PATH.exec(error.path))
          .filter((match): match is RegExpExecArray => match != null && csvTasks[+match[1]] != null)
          .map(match => tWithParms(
            "order.orderInfo.tasks.importTasksErrorLine",
            csvTasks[+match[1]].line,
            t("order.orderInfo.tasks.columns", match[2])
          ));
        const text = t("order.orderInfo.tasks.importTasksErrorText");
        return lineErrors.length > 0 ? `${text}\n\n${lineErrors.join("\n")}` : text;
      };

      const showImportError = (text: string) => {
        eventBus.$emit(MessageTypes.INFO, t("order.orderInfo.tasks.importTasksErrorTitle"), text);
      };

      const deleteImport = () => {
        OrderTaskService.deleteTaskImport(orderUuid() as string)
          .then(() => {
            currentImport.value = null;
          })
          .catch(() => { });
      };

      return {
        canManageImport,
        onImport,
        readFile,
        onDelete,
        fileInput,
        currentImport,
        tWithParms
      };
    }
  });
